/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { pages } from "../../../config/appData";
import PageHero from "../../components/PageHero";
import "./VenuesPage.css";
import { useAppDispatch, useGetBusinessData } from "../../../Utility";
import { onGetBusinesses } from "../../../Store/Slices/businessSlice";
import { tranformBusinessData } from "../../../utils/UtilityFunctions";
import EmptyState from "../../../Common/Components/EmptyState";
import toast from "react-hot-toast";
import WvManagedBadge from "../../components/WvManagedBadge";
import TopRibbon from "../../components/TopRibbon";
import {
  parseVenuesPathname,
  parseVenuesListingSplat,
  buildVenuesListingPath,
  legacySearchParamsToVenuesState,
} from "../../../utils/listingPaths";

const VENUE_SKELETON_COUNT = 4;
const VENUES_PAGE_SIZE = 50;
const VENUES_PER_PAGE = 20;
const PARAM_PAGE = "page";
const PARAM_SEARCH = "search";
const PARAM_PRICE_MIN = "priceMin";
const PARAM_PRICE_MAX = "priceMax";
const PARAM_VENUE_TYPE = "venueType";
const PARAM_TOP_RANK = "topRank";

function VenuesPage() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const businessData = useGetBusinessData();
  const businessesList = businessData?.businessesList ?? [];
  const isLoading = businessData?.getBusinessesLoading;
  const navigate = useNavigate();
  const { type, rest: venuesRest } = useMemo(
    () => parseVenuesPathname(location.pathname),
    [location.pathname],
  );
  const listingState = useMemo(
    () => parseVenuesListingSplat(venuesRest),
    [venuesRest],
  );
  const city = listingState.city;
  const pageParam = listingState.page || "1";
  const currentPage = Math.max(1, parseInt(pageParam, 10) || 1);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [activeImageByVenueId, setActiveImageByVenueId] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(true);
  const hasTopRank = listingState.hasTopRank;

  useEffect(() => {
    const raw = location.search;
    if (!raw || raw.length <= 1) return;
    const leg = legacySearchParamsToVenuesState(
      new URLSearchParams(raw.slice(1)),
    );
    navigate(buildVenuesListingPath(type, leg), { replace: true });
  }, [location.search, type, navigate]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 900) {
      setFiltersOpen(false);
    }
  }, []);

  const setPage = (p) => {
    const pageVal = p <= 1 ? "1" : String(p);
    navigate(
      buildVenuesListingPath(type, { ...listingState, page: pageVal }),
      { replace: true },
    );
  };

  const handleLoadMore = () => {
    dispatch(
      onGetBusinesses({
        limit: VENUES_PAGE_SIZE,
        offset: businessesList.length,
        append: true,
        ...(city ? { city } : {}),
      }),
    );
  };

  const searchQuery = listingState.searchQuery;
  const priceMin = listingState.priceMin;
  const priceMax = listingState.priceMax;
  const venueTypeFilter = listingState.venueTypeFilter;
  const topRankParam = listingState.topRankParam;

  const updateFilter = (key, value) => {
    const trimmed =
      value === "" || value == null ? "" : String(value).trim();
    const nextState = { ...listingState };
    if (key === PARAM_SEARCH) nextState.searchQuery = trimmed;
    else if (key === PARAM_PRICE_MIN) nextState.priceMin = trimmed;
    else if (key === PARAM_PRICE_MAX) nextState.priceMax = trimmed;
    else if (key === PARAM_VENUE_TYPE) nextState.venueTypeFilter = trimmed;
    else if (key === PARAM_TOP_RANK) nextState.topRankParam = trimmed;
    if (key !== PARAM_PAGE) nextState.page = "1";
    navigate(buildVenuesListingPath(type, nextState), { replace: true });
  };

  const clearFilters = () => {
    navigate(
      buildVenuesListingPath(type, {
        ...listingState,
        searchQuery: "",
        priceMin: "",
        priceMax: "",
        venueTypeFilter: "",
        topRankParam: "",
        hasTopRank: false,
        page: "1",
      }),
      { replace: true },
    );
  };

  const hasActiveFilters = !!(
    searchQuery ||
    priceMin ||
    priceMax ||
    venueTypeFilter ||
    topRankParam
  );

  const copyFilterLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied! Share it to keep the same filters.");
  };

  const rawVenues = useMemo(() => {
    let list = (businessesList || []).filter(
      (b) => b.business_type === "venue"
    );

    if (type !== "all") {
      list = list.filter((b) => b.venue?.venue_type?.toLowerCase() === type.toLowerCase());
    }

    const topRank = list
      .filter((b) => b.top_rank !== null)
      .sort((a, b) => a.top_rank - b.top_rank);

    const others = list.filter((b) => b.top_rank === null);

    if (hasTopRank) {
      return topRank;
    }

    return [...topRank, ...others];
  }, [businessesList, hasTopRank, type]);
  const venueTypes = useMemo(() => {
    const set = new Set();
    rawVenues.forEach((b) => {
      const t = b.venue?.venue_type || b.business_type;
      if (t) set.add(String(t).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rawVenues]);

  const filteredVenues = useMemo(() => {
    let list = rawVenues;
    if ((city || "").trim()) {
      const cityLower = city.trim().toLowerCase();
      list = list.filter(
        (item) => (item.city || "").trim().toLowerCase() === cityLower,
      );
    }
    if (topRankParam === "1-10") {
      list = list.filter((item) => {
        const r = item.top_rank;
        if (r == null || r === "") return false;
        const n = Number(r);
        return !Number.isNaN(n) && n >= 1 && n <= 10;
      });
    }
    const q = (searchQuery || "").trim().toLowerCase();
    if (q) {
      list = list.filter((item) => {
        const name = (item.business_name || "").toLowerCase();
        const cityVal = (item.city || "").toLowerCase();
        const venueType = (item.venue?.venue_type || "").toLowerCase();
        const address = (item.venue?.full_address || "").toLowerCase();
        const tags = (
          Array.isArray(item.tags)
            ? item.tags.join(" ")
            : String(item.tags || "")
        ).toLowerCase();
        const desc = (item.venue?.description || "").toLowerCase();
        return (
          name.includes(q) ||
          cityVal.includes(q) ||
          venueType.includes(q) ||
          address.includes(q) ||
          tags.includes(q) ||
          desc.includes(q)
        );
      });
    }
    const minP = priceMin !== "" && priceMin !== null ? Number(priceMin) : null;
    const maxP = priceMax !== "" && priceMax !== null ? Number(priceMax) : null;
    if (minP != null || maxP != null) {
      list = list.filter((item) => {
        const vMin = Number(item.price_min) || 0;
        const vMax = Number(item.price_max) || 0;
        if (minP != null && maxP != null) return vMin <= maxP && vMax >= minP;
        if (minP != null) return vMax >= minP;
        return vMin <= maxP;
      });
    }
    if (venueTypeFilter) {
      list = list.filter((item) => {
        const t = (
          item.venue?.venue_type ||
          item.business_type ||
          ""
        ).toLowerCase();
        return t === venueTypeFilter.toLowerCase();
      });
    }
    return tranformBusinessData(list, true);
  }, [
    rawVenues,
    city,
    searchQuery,
    priceMin,
    priceMax,
    venueTypeFilter,
    topRankParam,
  ]);

  const totalFiltered = filteredVenues.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / VENUES_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedVenues = useMemo(
    () =>
      filteredVenues.slice(
        (safePage - 1) * VENUES_PER_PAGE,
        safePage * VENUES_PER_PAGE,
      ),
    [filteredVenues, safePage],
  );
  const hasMoreFromApi = businessesList.length >= VENUES_PAGE_SIZE;
  const showLoadMore = totalFiltered > 0 && !isLoading && hasMoreFromApi;
  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getCardImages = (item) => {
    const images = Array.isArray(item?.images)
      ? item.images.filter(Boolean)
      : [];
    if (images.length > 0) return images;
    return item?.image ? [item.image] : [];
  };

  const getActiveImageIndex = (id, totalImages) => {
    if (!totalImages) return 0;
    const raw = activeImageByVenueId[id] ?? 0;
    return Math.min(Math.max(Number(raw) || 0, 0), totalImages - 1);
  };

  const updateVenueCardImage = (id, index) => {
    setActiveImageByVenueId((prev) => ({ ...prev, [id]: index }));
  };

  if (isLoading) {
    return (
      <div className="venues-page">
        <PageHero
          title={
            type === "all"
              ? pages.venues.title
              : pages.venues.titleWithCity(type, city)
          }
          subtitle={
            city && type !== "all"
              ? pages.venues.subtitleWithCity(type, city)
              : pages.venues.subtitle
          }
          count={
            filteredVenues.length > 0
              ? `(${filteredVenues.length} Venues)`
              : null
          }
        />
        <div className="venues-container">
          <div className="venues-list">
            {Array.from({ length: VENUE_SKELETON_COUNT }, (_, i) => (
              <div key={i} className="venue-list-card venue-skeleton-card">
                <div className="venue-skeleton-media skeleton-pulse" />
                <div className="venue-skeleton-body">
                  <div className="venue-skeleton-title skeleton-pulse" />
                  <div className="venue-skeleton-line skeleton-pulse" />
                  <div className="venue-skeleton-pills">
                    <span className="venue-skeleton-pill skeleton-pulse" />
                    <span className="venue-skeleton-pill skeleton-pulse" />
                  </div>
                  <div className="venue-skeleton-line venue-skeleton-line-short skeleton-pulse" />
                  <div className="venue-skeleton-line venue-skeleton-line-short skeleton-pulse" />
                  <div className="venue-skeleton-tags">
                    <span className="venue-skeleton-tag skeleton-pulse" />
                    <span className="venue-skeleton-tag skeleton-pulse" />
                    <span className="venue-skeleton-tag skeleton-pulse" />
                  </div>
                  <div className="venue-skeleton-bottom">
                    <div className="venue-skeleton-price skeleton-pulse" />
                    <div className="venue-skeleton-btn skeleton-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="venues-page">
      <PageHero
        title={
          type === "all"
            ? pages.venues.title
            : pages.venues.titleWithCity(type, city)
        }
        subtitle={
          city && type !== "all"
            ? pages.venues.subtitleWithCity(type, city)
            : pages.venues.subtitle
        }
        count={
          filteredVenues.length > 0 ? `(${filteredVenues.length} Venues)` : null
        }
      />
      <div className="venues-container">
        <div className="venues-layout">
          <aside className="list-filters-card list-filters-sidebar">
            <div className="list-filters-head">
              <span className="list-filters-title">Filters</span>
              <div className="list-filters-actions">
                <button
                  type="button"
                  className="list-filter-btn list-filter-mobile-toggle"
                  onClick={() => setFiltersOpen((prev) => !prev)}
                >
                  {filtersOpen ? "Hide" : "Show"}
                </button>
                {hasActiveFilters && (
                  <>
                  <button
                    type="button"
                    className="list-filter-btn list-filter-copy"
                    onClick={copyFilterLink}
                    title="Copy link with filters"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden
                    >
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    Share link
                  </button>
                  <button
                    type="button"
                    className="list-filter-btn list-filter-clear-all"
                    onClick={clearFilters}
                  >
                    Clear all
                  </button>
                  </>
                )}
              </div>
            </div>
            <div className={`list-filters-body ${filtersOpen ? "" : "list-filters-body-collapsed"}`}>
              <div className="list-filter-field list-filter-search-wrap">
                <label htmlFor="venue-search" className="list-filter-label">
                  Search
                </label>
                <span className="list-filter-input-wrap">
                  <input
                    id="venue-search"
                    type="text"
                    className="list-filter-input list-filter-search-input"
                    placeholder="Name, city, type, address..."
                    value={searchQuery}
                    onChange={(e) => updateFilter(PARAM_SEARCH, e.target.value)}
                  />
                </span>
              </div>
              <div className="list-filter-field list-filter-price-wrap">
                <span className="list-filter-label">Price range (₹)</span>
                <div className="list-filter-price-row">
                  <input
                    id="venue-price-min"
                    type="number"
                    min={0}
                    className="list-filter-input list-filter-price"
                    placeholder="Min"
                    value={priceMin}
                    onChange={(e) =>
                      updateFilter(PARAM_PRICE_MIN, e.target.value)
                    }
                  />
                  <span className="list-filter-price-sep">–</span>
                  <input
                    id="venue-price-max"
                    type="number"
                    min={0}
                    className="list-filter-input list-filter-price"
                    placeholder="Max"
                    value={priceMax}
                    onChange={(e) =>
                      updateFilter(PARAM_PRICE_MAX, e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="list-filter-field">
                <label
                  htmlFor="venue-type-filter"
                  className="list-filter-label"
                >
                  Venue type
                </label>
                <select
                  id="venue-type-filter"
                  className="list-filter-input list-filter-select"
                  value={venueTypeFilter}
                  onChange={(e) =>
                    updateFilter(PARAM_VENUE_TYPE, e.target.value)
                  }
                >
                  <option value="">All types</option>
                  {venueTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {hasActiveFilters && (
              <div className={`list-filters-chips ${filtersOpen ? "" : "list-filters-chips-collapsed"}`}>
                {searchQuery && (
                  <span className="list-filter-chip">
                    Search: {searchQuery}
                  </span>
                )}
                {(priceMin || priceMax) && (
                  <span className="list-filter-chip">
                    Price: {priceMin || "0"} – {priceMax || "∞"}
                  </span>
                )}
                {venueTypeFilter && (
                  <span className="list-filter-chip">
                    Type: {venueTypeFilter}
                  </span>
                )}
              </div>
            )}
          </aside>
          {filteredVenues.length > 0 ? (
            <div className="venues-list-wrap">
              <div className="venues-list venues-grid">
                {paginatedVenues.map((venue) => {
                  const isExpanded = expandedIds.has(venue.id);
                  const cardImages = getCardImages(venue);
                  const activeImageIndex = getActiveImageIndex(
                    venue.id,
                    cardImages.length,
                  );
                  const thumbnailImages = cardImages.slice(0, 3);
                  const hiddenImagesCount = Math.max(cardImages.length - 3, 0);
                  const activeImageSrc =
                    cardImages[activeImageIndex] || venue.image || "";
                  const guestsText =
                    venue.capacity && typeof venue.capacity === "object"
                      ? `${venue.capacity.min}-${venue.capacity.max} Guests`
                      : `${venue.capacity} Guests`;

                  const plateLabel = venue.pricePerPlate?.discounted ?? null;

                  return (
                    <div key={venue.id} className="venue-list-card">
                      <div
                        className="venue-list-media"
                        onClick={() => navigate(`/venue/${venue.id}`)}
                        role="button"
                        tabIndex={0}
                      >
                        {venue.top_rank != null && !Number.isNaN(Number(venue.top_rank)) && (
                          <TopRibbon label="Top Venues" />
                        )}
                        <img
                          src={activeImageSrc}
                          alt={venue.name}
                          className="venue-list-image"
                        />
                        {cardImages.length > 1 && (
                          <>
                            <button
                              type="button"
                              className="image-nav-btn prev-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const next =
                                  (activeImageIndex - 1 + cardImages.length) %
                                  cardImages.length;
                                updateVenueCardImage(venue.id, next);
                              }}
                              aria-label={`Previous image for ${venue.name}`}
                            >
                              ‹
                            </button>
                            <button
                              type="button"
                              className="image-nav-btn next-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const next =
                                  (activeImageIndex + 1) % cardImages.length;
                                updateVenueCardImage(venue.id, next);
                              }}
                              aria-label={`Next image for ${venue.name}`}
                            >
                              ›
                            </button>
                          </>
                        )}
                        <div className="venue-list-overlay-badges">
                          {/* <span className="venue-list-badge">{venue.type}</span> */}
                          <div className="venue-list-tags-overlay">
                            {(typeof venue?.tags === "string"
                              ? venue.tags.replace(/'/g, "").split(",")
                              : Array.isArray(venue?.tags)
                                ? venue.tags
                                : []
                            )
                              .slice(0, 3)
                              .map((tag) => (
                                <WvManagedBadge key={tag} text={tag} />
                              ))}
                          </div>
                        </div>
                        <div className="venue-list-thumbs" aria-hidden="true">
                          {thumbnailImages.map((img, index) => (
                            <img
                              key={`${venue.id}-${index}`}
                              src={img}
                              alt=""
                              className="venue-list-thumb"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                updateVenueCardImage(venue.id, index);
                              }}
                            />
                          ))}
                          {hiddenImagesCount > 0 && (
                            <button
                              type="button"
                              className="venue-list-thumb venue-list-thumb-view-all"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/venue/${venue.id}`);
                              }}
                              aria-label={`View all ${cardImages.length} images for ${venue.name}`}
                            >
                              <span className="venue-list-thumb-view-all-count">
                                +{hiddenImagesCount}
                              </span>
                              <span className="venue-list-thumb-view-all-text">
                                View All
                              </span>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="venue-list-body">
                        <div className="venue-list-top">
                          <div className="venue-list-titleblock">
                            <h3 className="venue-list-name">{venue.name}</h3>
                            <p className="venue-list-address">
                              {venue.address}
                            </p>
                          </div>
                          <div className="venue-list-pills">
                            <span className="venue-pill">{guestsText}</span>
                          </div>
                        </div>

                        {venue.description ? (
                          <p
                            className={`venue-list-desc ${isExpanded ? "expanded" : ""}`}
                          >
                            {venue.description}{" "}
                            <button
                              type="button"
                              className="venue-readmore"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleExpanded(venue.id);
                              }}
                            >
                              {isExpanded ? "Read less" : "Read more"}
                            </button>
                          </p>
                        ) : null}

                        <div className="venue-list-bottom">
                          <div className="venue-food">
                            {Array.isArray(venue.food) &&
                              venue.food.map((f) => (
                                <span
                                  key={f}
                                  className={`venue-food-pill ${f.toLowerCase().includes("non") ? "nonveg" : "veg"}`}
                                >
                                  {f}
                                </span>
                              ))}
                          </div>

                          <div className="venue-price">
                            <span
                              className="venue-price-icon"
                              aria-hidden="true"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="12" cy="12" r="8" />
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="5"
                                  strokeOpacity="0.35"
                                />
                              </svg>
                            </span>
                            {/* {plateOld ? (
                          <span className="venue-price-old">{plateOld}</span>
                        ) : null} */}
                            {plateLabel ? (
                              <span className="venue-price-new">
                                {plateLabel}
                              </span>
                            ) : null}
                            {plateLabel ? (
                              <span className="venue-price-unit">/Plate</span>
                            ) : null}
                          </div>

                          <div className="venue-actions">
                            {/* <button
                          type="button"
                          className="venue-action-icon"
                          aria-label="Shortlist"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                          </svg>
                        </button> */}
                            <button
                              type="button"
                              className="venue-action-primary"
                              onClick={() => navigate(`/venue/${venue.id}`)}
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {showLoadMore && (
                <div className="venues-pagination">
                  <div className="venues-pagination-info">
                    Page {safePage} of {totalPages}
                    {totalFiltered > 0 && (
                      <span className="venues-pagination-count">
                        ({totalFiltered} venues)
                      </span>
                    )}
                  </div>
                  <div className="venues-pagination-controls">
                    <button
                      type="button"
                      className="venues-pagination-btn"
                      disabled={safePage <= 1}
                      onClick={() => setPage(safePage - 1)}
                      aria-label="Previous page"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="venues-pagination-btn"
                      disabled={safePage >= totalPages}
                      onClick={() => setPage(safePage + 1)}
                      aria-label="Next page"
                    >
                      Next
                    </button>
                  </div>
                  {showLoadMore && (
                    <button
                      type="button"
                      className="venues-load-more-btn"
                      onClick={handleLoadMore}
                      disabled={isLoading}
                    >
                      {isLoading ? "Loading…" : "Load more venues"}
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="venues-list-wrap">
              <EmptyState
                title="No venues found"
                message={pages.venues.noResults(city)}
                action={
                  city ? (
                    <button type="button" onClick={() => navigate(`/`)}>
                      View all cities
                    </button>
                  ) : null
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VenuesPage;
